import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import TableLoadingRow from "./shared/TableLoadingRow";
import { listActiveStudents, listFrequencies } from "../services/student";
import { listClassrooms } from "../services/classroom";
import {
  anullPayment,
  downloadBulkReceipt,
  downloadExtracto,
  downloadReceipt,
  downloadTaxRemainderReceipt,
  listStudentExtraconfigs,
  anullStudentTax,
  payTaxRemainder,
  removeExtra,
  removeFine,
  removePropina,
  removeStudentTax,
  revertExtraPayment,
  updateExtraAmount,
} from "../services/payment";
import { levelKey } from "../utils/classesLabels";
import { canRemoveTaxEmolument, getSucursalInfo, getUserInfo, hasSpecialHourSchedule } from "../services/auth";
import { getAuthority } from "../services/authority";
import { useConfirm } from "../hook/useConfirm";
import {
  buildPropinaRows,
  formatCurrency,
  getPaymentDebt,
  getPropinaRemaining,
  getTotalOverdueDebt,
  isPaymentDelayed,
  monthName,
  monthShortName,
  PAYMENT_METHODS,
} from "../utils/paymentHelpers";

const PAGE_SIZE = 6;
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);

const isAdmin = () => {
  const authorities = getAuthority() || [];
  return ["admin", "superadmin", "administrative"].some((role) => authorities.includes(role));
};

// Legacy profile codes for the "Remover" gate below — see sge_infantil_core's
// authorityMap in routes/user.js: '004' = Director(a), '005' = Super Admin,
// '011' = Gestor Administrativo.
const REMOVE_PROPINA_PROFILE_CODES = ["004", "005", "011"];

// Gate for the "Remover" action on an unpaid (status 0) monthly payment — deliberately
// stricter than isAdmin() above: only Director, Super Admin and Gestor Administrativo
// (the plain "Admin" profile does NOT qualify), since the removal is permanent and has
// no backend reversal.
//
// Checks profile.code first, not just the `authority` string isAdmin() relies on:
// `authority` is copied onto the user record at creation time and can go stale (e.g.
// an account promoted to Super Admin after creation keeps its old authority string —
// confirmed in production via sge_infantil_core's scripts/audit-user-permissions.js,
// where profile.code=005/Super Admin but authority="admin"). profile.code is always
// resolved fresh from the account's actual profile, so it doesn't have that problem.
const canRemovePropina = () => {
  if (REMOVE_PROPINA_PROFILE_CODES.includes(getUserInfo()?.profile?.code)) return true;
  const authorities = getAuthority() || [];
  return ["director", "superadmin", "administrative"].some((role) => authorities.includes(role));
};

// Gate for the "Remover Multa" action, shown on a propina line whenever it carries a
// fine (payment.fine > 0), regardless of paid/partial/unpaid status. Restricted to
// Admin, Super Admin and Gestor/Director Administrativo — see sge_infantil_core's
// authorityMap in routes/user.js: '002' = Admin, '005' = Super Admin, '011' = Gestor
// Administrativo. Checks profile.code first (see canRemovePropina's comment above for
// why authority alone can go stale), falling back to the authority string.
const REMOVE_FINE_PROFILE_CODES = ["002", "005", "011"];
const canRemoveFine = () => {
  if (REMOVE_FINE_PROFILE_CODES.includes(getUserInfo()?.profile?.code)) return true;
  const authorities = getAuthority() || [];
  return ["admin", "superadmin", "administrative"].some((role) => authorities.includes(role));
};

// Shared 3-way (Pago / Pago Parcialmente / Não Pago) badge styling — used for both the
// combined month status and the propina-only status inside the expanded detail, so the
// two never disagree (e.g. showing "Pago Parcialmente" outside and "Não Pago" inside).
const statusBadgeProps = (status, t) => {
  const s = Number(status);
  if (s === 1) return { className: "bg-success-focus text-success-main", label: t("propina.status.paid") };
  if (s === 2) return { className: "bg-warning-focus text-warning-main", label: t("propina.status.partial") };
  return { className: "bg-danger-focus text-danger-main", label: t("propina.status.unpaid") };
};

const ListaPropinasLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();

  const [registrations, setRegistrations] = useState([]);
  const [extraconfigsData, setExtraconfigsData] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterYear, setFilterYear] = useState(currentYear);
  const [name, setName] = useState("");
  const [level, setLevel] = useState("");
  const [classroomId, setClassroomId] = useState("");
  const [page, setPage] = useState(1);

  const [expandedStudentKey, setExpandedStudentKey] = useState(null);
  const [expandedPaymentIds, setExpandedPaymentIds] = useState([]);
  const [busyPaymentId, setBusyPaymentId] = useState(null);
  const [expandedAggregatedKeys, setExpandedAggregatedKeys] = useState([]);
  const [busyAggregatedPrint, setBusyAggregatedPrint] = useState(null);

  const [removeFineTarget, setRemoveFineTarget] = useState(null);
  const [removeFineReason, setRemoveFineReason] = useState("");
  const [removingFine, setRemovingFine] = useState(false);

  const [busyExtraRef, setBusyExtraRef] = useState(null);
  const [editExtraTarget, setEditExtraTarget] = useState(null);
  const [editExtraAmount, setEditExtraAmount] = useState(0);
  const [savingExtraAmount, setSavingExtraAmount] = useState(false);

  const [busyTaxPrint, setBusyTaxPrint] = useState(null);
  const [busyTaxRemove, setBusyTaxRemove] = useState(null);
  const [busyTaxAnull, setBusyTaxAnull] = useState(null);
  const [payTaxRemainderTarget, setPayTaxRemainderTarget] = useState(null);
  const [payTaxRemainderAmount, setPayTaxRemainderAmount] = useState(0);
  const [payTaxRemainderMethod, setPayTaxRemainderMethod] = useState(PAYMENT_METHODS[PAYMENT_METHODS.length - 1]);
  const [payTaxRemainderDate, setPayTaxRemainderDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [payingTaxRemainder, setPayingTaxRemainder] = useState(false);

  const fetchData = (year) => {
    setLoading(true);
    Promise.all([listActiveStudents(year), listStudentExtraconfigs()])
      .then(([regResp, extraResp]) => {
        setRegistrations(regResp.data || []);
        const extras = (extraResp.data || []).filter((item) => Number(item.year) === Number(year));
        setExtraconfigsData(extras);
      })
      .catch(() => {
        setRegistrations([]);
        setExtraconfigsData([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData(filterYear);
    setExpandedStudentKey(null);
    setExpandedPaymentIds([]);
    setExpandedAggregatedKeys([]);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  useEffect(() => {
    listFrequencies()
      .then((resp) => setFrequencies(resp.data || []))
      .catch(() => setFrequencies([]));
  }, []);

  // Turmas (classroom entity) are year-scoped, distinct from "Nível" (the
  // frequencies/level above) — refetched whenever the year filter changes.
  useEffect(() => {
    listClassrooms(filterYear)
      .then((resp) => setClassrooms(resp.data || []))
      .catch(() => setClassrooms([]));
  }, [filterYear]);

  const rows = useMemo(
    () => buildPropinaRows(registrations, extraconfigsData, frequencies, filterYear),
    [registrations, extraconfigsData, frequencies, filterYear],
  );

  // Maps studentId -> classroom, built from each classroom's allocated
  // student roster (there's no classroomId on the registration/student
  // record itself, only the reverse link via classroom.students).
  const studentClassroomMap = useMemo(() => {
    const map = new Map();
    classrooms.forEach((c) => {
      (c.students || []).forEach((s) => {
        map.set(String(s.id), c);
      });
    });
    return map;
  }, [classrooms]);

  const filtered = useMemo(() => {
    const term = name.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesName = !term || r.name?.toLowerCase().includes(term);
      const matchesLevel = !level || String(r.corseId) === String(level);
      const matchesClassroom =
        !classroomId ||
        String(studentClassroomMap.get(String(r.studentId))?.id) === String(classroomId);
      return matchesName && matchesLevel && matchesClassroom;
    });
  }, [rows, name, level, classroomId, studentClassroomMap]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const overdueCount = useMemo(
    () => filtered.filter((r) => getTotalOverdueDebt(r.payments) > 0).length,
    [filtered],
  );

  const clearFilters = () => {
    setName("");
    setLevel("");
    setClassroomId("");
    setPage(1);
  };

  const toggleStudent = (key) =>
    setExpandedStudentKey((cur) => (cur === key ? null : key));

  const togglePayment = (id) =>
    setExpandedPaymentIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const toggleAggregated = (key) =>
    setExpandedAggregatedKeys((cur) => (cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]));

  const openPdf = (resp, errorKey, title, filename) => {
    if (resp?.data?.pdf) {
      navigate("/documentos/pdf", { state: { url: resp.data.pdf, title, filename } });
    } else {
      toast.error(t(errorKey));
    }
  };

  const handleDownloadExtracto = async (row) => {
    setBusyPaymentId(`extracto-${row.registrationId}`);
    try {
      const resp = await downloadExtracto(row.registrationId);
      openPdf(
        resp,
        "propina.error.print",
        t("propina.documento.extracto", { name: row.name }),
        `extracto-${row.name}.pdf`,
      );
    } catch (err) {
      toast.error(t("propina.error.print"));
    } finally {
      setBusyPaymentId(null);
    }
  };

  const buildExtrasForPdf = (payment) =>
    (payment.extrasForMonth || []).map((extra) => ({
      extraId: extra.extraId || extra.id,
      description: extra.taxName,
      monthlyAmount: extra.recurringAmount || extra.total,
      status: extra.status,
    }));

  const handleDownloadReceipt = async (payment, row) => {
    setBusyPaymentId(payment.id);
    try {
      const resp = await downloadReceipt(payment.id, buildExtrasForPdf(payment));
      openPdf(
        resp,
        "propina.error.print",
        t("propina.documento.receipt", { month: monthName(payment.month), year: payment.year }),
        `comprovativo-${row?.name || payment.id}.pdf`,
      );
    } catch (err) {
      toast.error(t("propina.error.print"));
    } finally {
      setBusyPaymentId(null);
    }
  };

  // Prints the combined comprovativo for one "Pagamento Agregado" (a
  // bulkProcessPayments/payGroup transaction covering several months and/or
  // taxes at once) — same endpoint AmortizacaoLayer's bulk flow already uses
  // to print right after paying, just re-derived here from the invoice's own
  // payment/tax ids since this is printed later, from the list.
  const handlePrintAggregated = async (invoice, row, key) => {
    setBusyAggregatedPrint(key);
    try {
      const paymentIds = (invoice.payments || []).map((p) => p.id).filter(Boolean);
      const taxIds = (invoice.taxPayments || []).map((tp) => tp.id).filter(Boolean);
      const resp = await downloadBulkReceipt(row.registrationId, { paymentIds, taxIds });
      openPdf(
        resp,
        "propina.error.print",
        t("propina.aggregated.title"),
        `agregado-${row.name}.pdf`,
      );
    } catch (err) {
      toast.error(t("propina.error.print"));
    } finally {
      setBusyAggregatedPrint(null);
    }
  };

  const handleAnular = async (payment, row) => {
    if (!(await confirm(t("propina.confirm.anular")))) return;
    setBusyPaymentId(payment.id);
    try {
      const rawPayments = row.payments.map((p) => {
        const { extrasTotal, extrasUnpaid, extrasForMonth, combinedTotal, status, originalStatus, ...rest } = p;
        return { ...rest, status: originalStatus };
      });
      await anullPayment(payment.id, {
        payments: rawPayments,
        registrationId: row.registrationId,
        updatedBy: getUserInfo()?.id,
        studentId: row.studentId,
      });
      toast.success(t("propina.anular.success"));
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setBusyPaymentId(null);
    }
  };

  const openRemoveFineModal = (payment) => {
    setRemoveFineTarget(payment);
    setRemoveFineReason("");
  };

  const handleConfirmRemoveFine = async () => {
    if (!removeFineReason.trim()) {
      toast.error(t("propina.removeFine.reasonRequired"));
      return;
    }
    setRemovingFine(true);
    try {
      await removeFine(removeFineTarget.id, {
        reason: removeFineReason,
        sucursalId: getSucursalInfo()?.id?.toString() || "1",
      });
      toast.success(t("propina.removeFine.success"));
      setRemoveFineTarget(null);
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setRemovingFine(false);
    }
  };

  const handleRemoveExtra = async (extra, row) => {
    if (
      !(await confirm(t("propina.extras.confirmRemove", { name: extra.taxName || t("propina.extras.default") })))
    )
      return;
    setBusyExtraRef(`remove-${extra.reference}`);
    try {
      await removeExtra({
        studentId: row.studentId,
        sucursalId: getSucursalInfo()?.id,
        extraId: extra.extraId || extra.id,
        reference: extra.reference,
        registrationId: row.registrationId,
      });
      toast.success(t("propina.extras.removeSuccess"));
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setBusyExtraRef(null);
    }
  };

  const handleRevertExtraPayment = async (extra, row) => {
    if (
      !(await confirm(
        t("propina.extras.confirmRevert", { name: extra.taxName || t("propina.extras.default") }),
      ))
    )
      return;
    setBusyExtraRef(`revert-${extra.reference}`);
    try {
      await revertExtraPayment({
        studentId: row.studentId,
        sucursalId: getSucursalInfo()?.id,
        extraId: extra.extraId || extra.id,
        reference: extra.reference,
      });
      toast.success(t("propina.extras.revertSuccess"));
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setBusyExtraRef(null);
    }
  };

  const openEditExtraModal = (extra, row) => {
    setEditExtraTarget({ extra, row });
    setEditExtraAmount(parseFloat(extra.total) || 0);
  };

  const handleConfirmEditExtra = async () => {
    const amount = parseFloat(editExtraAmount);
    if (!amount || amount <= 0) {
      toast.error(t("propina.extras.invalidAmount"));
      return;
    }
    setSavingExtraAmount(true);
    try {
      const { extra, row } = editExtraTarget;
      await updateExtraAmount({
        studentId: row.studentId,
        sucursalId: getSucursalInfo()?.id,
        extraId: extra.extraId || extra.id,
        reference: extra.reference,
        newAmount: amount,
      });
      toast.success(t("propina.extras.editSuccess"));
      setEditExtraTarget(null);
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setSavingExtraAmount(false);
    }
  };

  const handlePayExtrasOnly = (payment, row) => {
    const unpaid = (payment.extrasForMonth || []).filter((e) => Number(e.status) === 0);
    if (unpaid.length === 0) {
      toast.warning(t("propina.extras.noneUnpaid"));
      return;
    }
    navigate(`/pagamentos/propinas/pagar/${payment.id}`, {
      state: { registration: row, extrasForPayment: unpaid, forcePaymentType: "extras" },
    });
  };

  // "Pagar Remanescente" — settles what's still owed on the propina/multa itself for a
  // month already left as Parcial (status 2). Distinct from "Pagar Extras": extras stay
  // on their own transaction, so extrasForPayment is always empty here, and from
  // "Pagar" (handlePayNow), which is only offered for months not yet paid at all.
  const handlePayRemainder = (payment, row) => {
    navigate(`/pagamentos/propinas/pagar/${payment.id}`, {
      state: { registration: row, extrasForPayment: [], forcePaymentType: "remainder" },
    });
  };

  const handlePayNow = (payment, row) => {
    const blockingTax = getRegistrationTaxBlocking(row);
    if (blockingTax) {
      toast.error(t("propina.error.registrationTaxPending"));
      return;
    }
    const paymentsInSameYear = row.payments.filter((p) => Number(p.year) === Number(payment.year));
    const previousUnpaidMonth = paymentsInSameYear.find(
      (p) => +p.month < +payment.month && +p.status === 0,
    );
    if (previousUnpaidMonth) {
      toast.error(
        t("propina.error.previousUnpaid", {
          month: monthName(payment.month),
          previousMonth: monthName(previousUnpaidMonth.month),
        }),
      );
      return;
    }
    navigate(`/pagamentos/propinas/pagar/${payment.id}`, {
      state: { registration: row, extrasForPayment: payment.extrasForMonth || [] },
    });
  };

  // Permanently deletes an unpaid (status 0) monthly payment — no reversal, so the
  // confirmation must be explicit about that (unlike handleAnular, which is reversible
  // by simply paying again).
  const handleRemovePropina = async (payment, row) => {
    if (!(await confirm(t("propina.confirm.removePropina")))) return;
    setBusyPaymentId(payment.id);
    try {
      await removePropina(payment.id);
      toast.success(t("propina.remove.success"));
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setBusyPaymentId(null);
    }
  };

  const handleGoToAmortization = (row) => {
    navigate(`/pagamentos/propinas/amortizar/${row.studentId}`, { state: { registration: row } });
  };

  // Outstanding balance of a tax/emolumento (status 2 = Pago Parcialmente).
  // outstandingAmount is the backend's authoritative figure; taxValue - paidAmount
  // is only a fallback for older records written before that field existed.
  const getTaxRemaining = (tax) => {
    if (Number(tax.status) !== 2) return 0;
    const owed =
      tax.outstandingAmount != null
        ? parseFloat(tax.outstandingAmount)
        : (parseFloat(tax.taxValue) || 0) - (parseFloat(tax.paidAmount) || 0);
    return Math.max(0, owed);
  };

  // A taxa de Inscrição/Renovação da registration, se ainda não estiver totalmente paga
  // (status !== 1/PAID). Sem `code` na entrada (registration criada antes desta mudança
  // carregar esse campo em taxPayments), devolve undefined — i.e. nunca bloqueia
  // retroactivamente registrations antigas.
  const getRegistrationTaxBlocking = (row) =>
    (row.taxPayments || []).find(
      (tp) => (tp.code === "registrationValue" || tp.code === "recurigRegistrationValue") && Number(tp.status) !== 1,
    );

  const openPayTaxRemainderModal = (tax, row) => {
    const remaining = getTaxRemaining(tax);
    setPayTaxRemainderTarget({ tax, row });
    setPayTaxRemainderAmount(remaining.toFixed(2));
    setPayTaxRemainderMethod(tax.paymentMethod || PAYMENT_METHODS[PAYMENT_METHODS.length - 1]);
    setPayTaxRemainderDate(new Date().toISOString().split("T")[0]);
  };

  const handleConfirmPayTaxRemainder = async () => {
    const { tax, row } = payTaxRemainderTarget;
    const remaining = getTaxRemaining(tax);
    const amount = Math.max(0, Math.min(parseFloat(payTaxRemainderAmount) || 0, remaining));
    if (!(amount > 0)) {
      toast.error(t("propina.extras.invalidAmount"));
      return;
    }
    setPayingTaxRemainder(true);
    try {
      await payTaxRemainder(tax.id, {
        registrationId: row.registrationId,
        updatedBy: getUserInfo()?.id,
        paymentMethod: payTaxRemainderMethod,
        amountPaid: amount,
        paymentDate: payTaxRemainderDate,
      });
      toast.success(t("propina.removeFine.success"));
      setPayTaxRemainderTarget(null);
      // Comprovativo desta transacção: usa `amount` (o que foi pago agora), não o
      // cumulativo que `fetchData` abaixo vai trazer de volta no estado da tax.
      try {
        const resp = await downloadTaxRemainderReceipt(tax.id, { amountPaid: amount });
        openPdf(resp, "propina.error.print", t("propina.section.taxes"), `taxa-${row.name}.pdf`);
      } catch (err) {
        toast.error(t("propina.error.print"));
      }
      fetchData(filterYear);
    } catch (err) {
      toast.error(t("propina.error.generic"));
    } finally {
      setPayingTaxRemainder(false);
    }
  };

  // Prints the tax's receipt: the remainder-specific layout ("Pago
  // Parcialmente", showing what was owed/paid/left) when it's only been
  // partially paid, or the regular taxes-report receipt once fully settled.
  const handlePrintTaxReceipt = async (tax, row) => {
    setBusyTaxPrint(tax.id);
    try {
      if (Number(tax.status) === 2) {
        const resp = await downloadTaxRemainderReceipt(tax.id, { amountPaid: tax.paidAmount });
        openPdf(resp, "propina.error.print", t("propina.section.taxes"), `taxa-${row.name}.pdf`);
      } else {
        const resp = await downloadBulkReceipt(row.registrationId, { taxIds: [tax.id] });
        openPdf(resp, "propina.error.print", t("propina.section.taxes"), `taxa-${row.name}.pdf`);
      }
    } catch (err) {
      toast.error(t("propina.error.print"));
    } finally {
      setBusyTaxPrint(null);
    }
  };

  // Reverts a paid/partially-paid tax charge back to unpaid — mirrors handleAnular
  // above (which does the same for a propina), but for a single tax/emolumento.
  // Unlike handleRemoveStudentTax below, this keeps the record (reversible by simply
  // paying again). Offered only for status 1/2 — see canRemoveTaxEmolument() for the
  // role gate.
  const handleAnularStudentTax = async (tax, row) => {
    if (!(await confirm(t("propina.tax.confirm.anular")))) return;
    setBusyTaxAnull(tax.id);
    try {
      await anullStudentTax(row.registrationId, tax.id, { canceledBy: getUserInfo()?.id });
      toast.success(t("propina.tax.anular.success"));
      fetchData(filterYear);
    } catch (err) {
      const apiError = err?.response?.data?.error;
      toast.error(typeof apiError === "string" ? apiError : t("propina.error.generic"));
    } finally {
      setBusyTaxAnull(null);
    }
  };

  // Permanently removes a still-unpaid (status 0) tax charge from the student's
  // registration — no reversal, mirrors handleRemovePropina above. A paid/partial
  // charge must be anulled first (handleAnularStudentTax) before it can be removed —
  // the backend itself only rejects a fully PAID one, but the button below is
  // deliberately restricted to status 0 to keep "Anular" and "Remover" mutually
  // exclusive, same as the propinas list above. See canRemoveTaxEmolument() for the
  // role gate.
  const handleRemoveStudentTax = async (tax, row) => {
    if (!(await confirm(t("propina.tax.confirm.remove")))) return;
    setBusyTaxRemove(tax.id);
    try {
      await removeStudentTax(row.registrationId, tax.id);
      toast.success(t("propina.tax.remove.success"));
      fetchData(filterYear);
    } catch (err) {
      const apiError = err?.response?.data?.error;
      toast.error(typeof apiError === "string" ? apiError : t("propina.tax.error.remove"));
    } finally {
      setBusyTaxRemove(null);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />
      {ConfirmModal}

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:users-group-rounded-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{filtered.length}</h6>
                <span className='text-secondary-light text-sm'>{t("propina.stat.total")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-danger-focus text-danger-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:alert-circle-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{overdueCount}</h6>
                <span className='text-secondary-light text-sm'>{t("propina.stat.overdue")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("propina.list.title")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("propina.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-outline-secondary d-flex align-items-center gap-2'
                onClick={() => fetchData(filterYear)}
                disabled={loading}
              >
                <Icon icon='ic:round-refresh' />
                {t("global.refresh")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("estudante.field.selectedYear")}</label>
                  <select
                    className='form-control form-select'
                    value={filterYear}
                    onChange={(e) => setFilterYear(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("propina.filter.name")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("propina.filter.name.placeholder")}
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t(levelKey("estudante.field.turma"))}</label>
                  <select
                    className='form-control form-select'
                    value={level}
                    onChange={(e) => {
                      setLevel(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("estudante.list.filter.all")}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("estudante.field.classroom")}</label>
                  <select
                    className='form-control form-select'
                    value={classroomId}
                    onChange={(e) => {
                      setClassroomId(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value=''>{t("estudante.list.filter.all")}</option>
                    {classrooms.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.className}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-lg-2'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2'
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                    {t("global.clean.fields")}
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("global.name")}</th>
                    <th scope='col'>{t(levelKey("estudante.field.turma"))}</th>
                    <th scope='col'>{t("propina.col.enrolledAt")}</th>
                    <th scope='col'>{t("estudante.field.selectedYear")}</th>
                    <th scope='col'>{t("propina.col.debt")}</th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <TableLoadingRow colSpan={7} label={t("estudante.list.loading")} />
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={7} className='text-center text-secondary-light py-32'>
                        {t("estudante.list.empty")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((row) => {
                    const isExpanded = expandedStudentKey === row.key;
                    const debt = getTotalOverdueDebt(row.payments);
                    const extractoBusy = busyPaymentId === `extracto-${row.registrationId}`;
                    return (
                      <React.Fragment key={row.key}>
                        <tr role='button' onClick={() => toggleStudent(row.key)} className={isExpanded ? "bg-neutral-50" : ""}>
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{row.name}</td>
                          <td>{row.level || "-"}</td>
                          <td>
                            {row.createdAt
                              ? new Date(row.createdAt).toLocaleDateString("pt-PT", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                              : "-"}
                          </td>
                          <td>{row.year}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {debt > 0 ? (
                              <button
                                type='button'
                                className='btn btn-danger btn-sm d-flex align-items-center gap-2'
                                title={t("propina.debt.tooltip")}
                                onClick={() => handleGoToAmortization(row)}
                              >
                                <Icon icon='mdi:alert-outline' />
                                {formatCurrency(debt)}
                              </button>
                            ) : (
                              <span className='text-success-main fw-semibold'>{formatCurrency(debt)}</span>
                            )}
                          </td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <button
                              type='button'
                              className='btn btn-primary-600 btn-sm d-flex align-items-center gap-2 ms-auto'
                              disabled={extractoBusy}
                              onClick={() => handleDownloadExtracto(row)}
                            >
                              <Icon icon='solar:printer-linear' />
                              {extractoBusy ? "..." : t("propina.action.printExtracto")}
                            </button>
                          </td>
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className='p-0'>
                              <div className='p-16 bg-neutral-25 border-top'>
                                {hasSpecialHourSchedule() && (
                                  <div className='mb-12 text-sm'>
                                    <span className='text-secondary-light'>{t("propina.field.scheduleType")}: </span>
                                    <strong>
                                      {row.scheduleType === "7h-15h"
                                        ? t("estudante.field.scheduleType.special")
                                        : t("estudante.field.scheduleType.standard")}
                                    </strong>
                                    <span className='text-secondary-light'> — {t("propina.field.monthlyPaymentBasis")}: </span>
                                    <strong>{formatCurrency(row.registrationMonthlyPayment)}</strong>
                                  </div>
                                )}
                                <h6 className='text-md fw-semibold mb-12 text-primary-600 d-flex align-items-center gap-2'>
                                  <Icon icon='solar:document-text-linear' />
                                  {t("propina.section.propinasExtras")}
                                </h6>
                                <div className='table-responsive'>
                                  <table className='table table-bordered table-sm mb-0 bg-base'>
                                    <thead>
                                      <tr>
                                        <th className='bg-neutral-50' style={{ width: 28 }} />
                                        <th className='bg-neutral-50' style={{ width: 80 }}>{t("propina.col.month")}</th>
                                        <th className='bg-neutral-50' style={{ width: 90 }}>{t("propina.col.propina")}</th>
                                        <th className='bg-neutral-50' style={{ width: 90 }}>{t("propina.col.extras")}</th>
                                        <th className='bg-neutral-50' style={{ width: 90 }}>{t("propina.col.total")}</th>
                                        <th className='bg-neutral-50' style={{ width: 90 }}>{t("propina.col.debt")}</th>
                                        <th className='bg-neutral-50' style={{ width: 110 }}>{t("global.status")}</th>
                                        <th className='bg-neutral-50 text-end'>{t("global.col.actions")}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {row.payments.map((payment) => {
                                        const isPaymentExpanded = expandedPaymentIds.includes(payment.id);
                                        const delayed = isPaymentDelayed(payment.limit) && +payment.status === 0;
                                        const paidOrPartial = Number(payment.status) === 1 || Number(payment.status) === 2;
                                        const monthDebt = getPaymentDebt(payment);
                                        const canManage =
                                          isAdmin() && payment.paymentMethod !== "Pagamento e Serviço";
                                        const busy = busyPaymentId === payment.id;
                                        const hasUnpaidExtras =
                                          Number(payment.status) === 2 &&
                                          (payment.extrasForMonth || []).some((e) => Number(e.status) === 0);
                                        const propinaRemaining = getPropinaRemaining(payment);
                                        const hasRemainder = Number(payment.status) === 2 && propinaRemaining > 0.01;
                                        return (
                                          <React.Fragment key={payment.id}>
                                            <tr
                                              role='button'
                                              onClick={() => togglePayment(payment.id)}
                                              className={delayed ? "bg-warning-focus" : ""}
                                            >
                                              <td>
                                                <Icon
                                                  icon={
                                                    isPaymentExpanded
                                                      ? "ic:round-expand-less"
                                                      : "ic:round-expand-more"
                                                  }
                                                  className='text-secondary-light text-xl'
                                                />
                                              </td>
                                              <td className='notranslate'>
                                                {monthShortName(payment.month)}/{payment.year}
                                              </td>
                                              <td>{formatCurrency(payment.total)}</td>
                                              <td>
                                                {payment.extrasTotal > 0 ? (
                                                  <span className='text-warning-main'>
                                                    {formatCurrency(payment.extrasTotal)}
                                                  </span>
                                                ) : (
                                                  "-"
                                                )}
                                              </td>
                                              <td>
                                                <strong className={payment.extrasTotal > 0 ? "text-info-main" : ""}>
                                                  {formatCurrency(payment.combinedTotal)}
                                                </strong>
                                              </td>
                                              <td>{formatCurrency(monthDebt)}</td>
                                              <td>
                                                <span
                                                  className={`px-12 py-4 radius-8 fw-medium text-sm ${
                                                    statusBadgeProps(payment.status, t).className
                                                  }`}
                                                >
                                                  {statusBadgeProps(payment.status, t).label}
                                                </span>
                                              </td>
                                              <td
                                                className='text-end'
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <div className='d-flex flex-wrap gap-1 justify-content-end'>
                                                  {paidOrPartial ? (
                                                    <>
                                                      {hasRemainder && (
                                                        <button
                                                          type='button'
                                                          className='btn btn-primary-600 btn-sm d-inline-flex align-items-center gap-1'
                                                          onClick={() => handlePayRemainder(payment, row)}
                                                        >
                                                          <Icon icon='solar:card-transfer-linear' />
                                                          {t("propina.action.payRemainder")}
                                                        </button>
                                                      )}
                                                      {canManage && (
                                                        <button
                                                          type='button'
                                                          className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1'
                                                          disabled={busy}
                                                          onClick={() => handleAnular(payment, row)}
                                                        >
                                                          <Icon icon='mdi:close-circle-outline' />
                                                          {t("propina.action.anular")}
                                                        </button>
                                                      )}
                                                      {payment.fine > 0 && canRemoveFine() && (
                                                        <button
                                                          type='button'
                                                          className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1'
                                                          onClick={() => openRemoveFineModal(payment)}
                                                        >
                                                          <Icon icon='mingcute:delete-2-line' />
                                                          {t("propina.action.removeFine")}
                                                        </button>
                                                      )}
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1'
                                                        disabled={busy}
                                                        onClick={() => handleDownloadReceipt(payment, row)}
                                                      >
                                                        <Icon icon='solar:printer-linear' />
                                                        {t("propina.action.receipt")}
                                                      </button>
                                                      {hasUnpaidExtras && (
                                                        <button
                                                          type='button'
                                                          className='btn btn-sm text-white d-inline-flex align-items-center gap-1'
                                                          style={{ backgroundColor: "#fa8c16" }}
                                                          onClick={() => handlePayExtrasOnly(payment, row)}
                                                        >
                                                          <Icon icon='solar:wallet-money-linear' />
                                                          {t("propina.action.payExtras")}
                                                        </button>
                                                      )}
                                                    </>
                                                  ) : (
                                                    <>
                                                      <button
                                                        type='button'
                                                        className='btn btn-primary-600 btn-sm d-inline-flex align-items-center gap-1'
                                                        disabled={!!getRegistrationTaxBlocking(row)}
                                                        title={
                                                          getRegistrationTaxBlocking(row)
                                                            ? t("propina.action.pay.locked")
                                                            : undefined
                                                        }
                                                        onClick={() => handlePayNow(payment, row)}
                                                      >
                                                        <Icon
                                                          icon={
                                                            getRegistrationTaxBlocking(row)
                                                              ? "mdi:lock-outline"
                                                              : "solar:dollar-minimalistic-linear"
                                                          }
                                                        />
                                                        {t("propina.action.pay")}
                                                      </button>
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1'
                                                        disabled={busy}
                                                        onClick={() => handleDownloadReceipt(payment, row)}
                                                      >
                                                        <Icon icon='solar:document-text-linear' />
                                                        {t("propina.action.invoice")}
                                                      </button>
                                                      {Number(payment.status) === 0 && canRemovePropina() && (
                                                        <button
                                                          type='button'
                                                          className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1'
                                                          disabled={busy}
                                                          onClick={() => handleRemovePropina(payment, row)}
                                                        >
                                                          <Icon icon='mingcute:delete-2-line' />
                                                          {t("propina.action.remove")}
                                                        </button>
                                                      )}
                                                      {payment.fine > 0 && canRemoveFine() && (
                                                        <button
                                                          type='button'
                                                          className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1'
                                                          onClick={() => openRemoveFineModal(payment)}
                                                        >
                                                          <Icon icon='mingcute:delete-2-line' />
                                                          {t("propina.action.removeFine")}
                                                        </button>
                                                      )}
                                                    </>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>

                                            {isPaymentExpanded && (
                                              <tr>
                                                <td colSpan={8} className='p-0'>
                                                  <div className='p-16 bg-base border-top'>
                                                    <table className='table table-bordered table-sm mb-0'>
                                                      <tbody>
                                                        <tr>
                                                          <th className='w-25 bg-neutral-50'>
                                                            {t("propina.detail.monthlyValue")}
                                                          </th>
                                                          <td>
                                                            {formatCurrency(
                                                              payment.registrationMonthlyPayment ?? payment.total,
                                                            )}
                                                          </td>
                                                          <th className='w-25 bg-neutral-50'>{t("propina.col.year")}</th>
                                                          <td>{payment.year}</td>
                                                        </tr>
                                                        <tr>
                                                          <th className='bg-neutral-50'>{t("propina.col.month")}</th>
                                                          <td className='notranslate'>{monthName(payment.month)}</td>
                                                          <th className='bg-neutral-50'>{t("propina.detail.discount")}</th>
                                                          <td>{formatCurrency(payment.discount)}</td>
                                                        </tr>
                                                        <tr>
                                                          <th className='bg-neutral-50'>{t("propina.detail.iva")}</th>
                                                          <td>{getSucursalInfo()?.ivaPercentage || 0}%</td>
                                                          {payment.fine > 0 ? (
                                                            <>
                                                              <th className='bg-neutral-50'>{t("propina.detail.fine")}</th>
                                                              <td>{formatCurrency(payment.fine)}</td>
                                                            </>
                                                          ) : (
                                                            <>
                                                              <th className='bg-neutral-50'>
                                                                {t("propina.detail.propinaStatus")}
                                                              </th>
                                                              <td>
                                                                <span
                                                                  className={`px-12 py-4 radius-8 fw-medium text-sm ${
                                                                    statusBadgeProps(payment.originalStatus, t).className
                                                                  }`}
                                                                >
                                                                  {statusBadgeProps(payment.originalStatus, t).label}
                                                                </span>
                                                              </td>
                                                            </>
                                                          )}
                                                        </tr>
                                                        {payment.fine > 0 && (
                                                          <tr>
                                                            <th className='bg-neutral-50'>
                                                              {t("propina.detail.propinaStatus")}
                                                            </th>
                                                            <td colSpan={3}>
                                                              <span
                                                                className={`px-12 py-4 radius-8 fw-medium text-sm ${
                                                                  statusBadgeProps(payment.originalStatus, t).className
                                                                }`}
                                                              >
                                                                {statusBadgeProps(payment.originalStatus, t).label}
                                                              </span>
                                                            </td>
                                                          </tr>
                                                        )}
                                                        <tr>
                                                          <th className='bg-neutral-50'>
                                                            {t("propina.detail.paymentDate")}
                                                          </th>
                                                          <td>
                                                            {payment.paymentDate
                                                              ? new Date(payment.paymentDate).toLocaleDateString(
                                                                  "pt-PT",
                                                                )
                                                              : "-"}
                                                          </td>
                                                          <th className='bg-neutral-50'>
                                                            {t("propina.detail.paymentMethod")}
                                                          </th>
                                                          <td>{payment.paymentMethod || "-"}</td>
                                                        </tr>
                                                        <tr>
                                                          <th className='bg-neutral-50'>{t("propina.col.limitDate")}</th>
                                                          <td colSpan={3}>
                                                            {payment.limit
                                                              ? new Date(payment.limit).toLocaleDateString("pt-PT")
                                                              : "-"}
                                                          </td>
                                                        </tr>
                                                        <tr>
                                                          <th className='bg-neutral-50'>
                                                            {t("propina.detail.paidAmount")}
                                                          </th>
                                                          <td>
                                                            <span className='text-success-main fw-semibold'>
                                                              {formatCurrency(payment.combinedTotal - monthDebt)}
                                                            </span>
                                                          </td>
                                                          <th className='bg-neutral-50'>
                                                            {t("propina.detail.remainingAmount")}
                                                          </th>
                                                          <td>
                                                            <span
                                                              className={
                                                                monthDebt > 0.01
                                                                  ? "text-danger-main fw-semibold"
                                                                  : "text-success-main fw-semibold"
                                                              }
                                                            >
                                                              {formatCurrency(monthDebt)}
                                                            </span>
                                                          </td>
                                                        </tr>
                                                        {payment.extrasTotal > 0 && (
                                                          <tr>
                                                            <th className='bg-neutral-50'>{t("propina.col.extras")}</th>
                                                            <td colSpan={3}>
                                                              <div className='d-flex flex-column gap-8'>
                                                                {payment.extrasForMonth.map((extra, idx) => {
                                                                  const extraBusy = busyExtraRef === `remove-${extra.reference}` || busyExtraRef === `revert-${extra.reference}`;
                                                                  return (
                                                                    <div
                                                                      key={idx}
                                                                      className='d-flex align-items-center flex-wrap gap-2'
                                                                    >
                                                                      <span
                                                                        className={
                                                                          Number(extra.status) === 1
                                                                            ? "px-8 py-2 radius-8 text-xs bg-success-focus text-success-main"
                                                                            : "px-8 py-2 radius-8 text-xs bg-danger-focus text-danger-main"
                                                                        }
                                                                      >
                                                                        {Number(extra.status) === 1
                                                                          ? t("propina.status.paid")
                                                                          : t("propina.status.unpaid")}
                                                                      </span>
                                                                      <strong>{extra.taxName || t("propina.extras.default")}</strong>
                                                                      <span>{formatCurrency(extra.total)}</span>
                                                                      {Number(extra.status) === 1 ? (
                                                                        <button
                                                                          type='button'
                                                                          className='btn btn-outline-warning btn-sm py-2 px-8'
                                                                          disabled={extraBusy}
                                                                          onClick={() => handleRevertExtraPayment(extra, row)}
                                                                        >
                                                                          {t("propina.extras.revert")}
                                                                        </button>
                                                                      ) : (
                                                                        <>
                                                                          <button
                                                                            type='button'
                                                                            className='btn btn-outline-secondary btn-sm py-2 px-8'
                                                                            disabled={extraBusy}
                                                                            onClick={() => openEditExtraModal(extra, row)}
                                                                          >
                                                                            <Icon icon='solar:pen-2-linear' />
                                                                          </button>
                                                                          <button
                                                                            type='button'
                                                                            className='btn btn-outline-danger btn-sm py-2 px-8'
                                                                            disabled={extraBusy}
                                                                            onClick={() => handleRemoveExtra(extra, row)}
                                                                          >
                                                                            <Icon icon='mingcute:delete-2-line' />
                                                                          </button>
                                                                        </>
                                                                      )}
                                                                    </div>
                                                                  );
                                                                })}
                                                                <div className='fw-semibold text-warning-main'>
                                                                  {t("propina.detail.extrasTotal")}:{" "}
                                                                  {formatCurrency(payment.extrasTotal)}
                                                                </div>
                                                              </div>
                                                            </td>
                                                          </tr>
                                                        )}
                                                        {payment.extrasTotal > 0 && (
                                                          <tr>
                                                            <th className='bg-neutral-50'>
                                                              {t("propina.detail.combinedTotal")}
                                                            </th>
                                                            <td colSpan={3}>
                                                              {Number(payment.status) === 1 ? (
                                                                <strong className='text-success-main'>
                                                                  {t("propina.status.paid")}
                                                                </strong>
                                                              ) : (
                                                                <strong className='text-primary-600'>
                                                                  {formatCurrency(payment.combinedTotal)}
                                                                </strong>
                                                              )}
                                                            </td>
                                                          </tr>
                                                        )}
                                                        <tr>
                                                          <th className='bg-neutral-50'>
                                                            {t("propina.detail.reference")}
                                                          </th>
                                                          <td colSpan={3}>{payment.reference || "-"}</td>
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>

                                {row.taxPayments && row.taxPayments.length > 0 && (
                                  <div className='mt-16'>
                                    <h6 className='text-md fw-semibold mb-12 text-primary-600 d-flex align-items-center gap-2'>
                                      <Icon icon='solar:bill-list-linear' />
                                      {t("propina.section.taxes")}
                                    </h6>
                                    <div className='table-responsive'>
                                      <table className='table table-bordered table-sm mb-0 bg-base'>
                                        <thead>
                                          <tr>
                                            <th className='bg-neutral-50'>{t("bulk.col.taxName")}</th>
                                            <th className='bg-neutral-50'>{t("propina.col.total")}</th>
                                            <th className='bg-neutral-50'>{t("bulk.col.paidAmount")}</th>
                                            <th className='bg-neutral-50'>{t("propina.col.debt")}</th>
                                            <th className='bg-neutral-50'>{t("global.status")}</th>
                                            <th className='bg-neutral-50 text-end'>{t("global.col.actions")}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {row.taxPayments.map((tax) => {
                                            const status = Number(tax.status);
                                            const taxValue = parseFloat(tax.taxValue) || 0;
                                            const paid = status === 1 ? taxValue : parseFloat(tax.paidAmount || 0);
                                            const remaining = getTaxRemaining(tax);
                                            const printing = busyTaxPrint === tax.id;
                                            return (
                                              <tr key={tax.id}>
                                                <td>{tax.taxName}</td>
                                                <td>{formatCurrency(taxValue)}</td>
                                                <td>{formatCurrency(paid)}</td>
                                                <td>{remaining > 0.01 ? formatCurrency(remaining) : "-"}</td>
                                                <td>
                                                  <span
                                                    className={`px-12 py-4 radius-8 fw-medium text-sm ${
                                                      statusBadgeProps(status, t).className
                                                    }`}
                                                  >
                                                    {statusBadgeProps(status, t).label}
                                                  </span>
                                                </td>
                                                <td className='text-end'>
                                                  <div className='d-flex flex-wrap gap-1 justify-content-end'>
                                                    {status === 2 && (
                                                      <button
                                                        type='button'
                                                        className='btn btn-primary-600 btn-sm d-inline-flex align-items-center gap-1'
                                                        onClick={() => openPayTaxRemainderModal(tax, row)}
                                                      >
                                                        <Icon icon='solar:card-transfer-linear' />
                                                        {t("propina.action.payRemainder")}
                                                      </button>
                                                    )}
                                                    <button
                                                      type='button'
                                                      className='btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1'
                                                      disabled={printing}
                                                      onClick={() => handlePrintTaxReceipt(tax, row)}
                                                    >
                                                      <Icon icon='solar:printer-linear' />
                                                      {printing ? "..." : t("propina.aggregated.action.print")}
                                                    </button>
                                                    {(status === 1 || status === 2) && canRemoveTaxEmolument() && (
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1'
                                                        disabled={busyTaxAnull === tax.id}
                                                        onClick={() => handleAnularStudentTax(tax, row)}
                                                      >
                                                        <Icon icon='mdi:close-circle-outline' />
                                                        {t("propina.tax.action.anular")}
                                                      </button>
                                                    )}
                                                    {status === 0 && canRemoveTaxEmolument() && (
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1'
                                                        disabled={busyTaxRemove === tax.id}
                                                        onClick={() => handleRemoveStudentTax(tax, row)}
                                                      >
                                                        <Icon icon='mingcute:delete-2-line' />
                                                        {t("propina.tax.action.remove")}
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {row.aggregatedInvoices && row.aggregatedInvoices.length > 0 && (
                                  <div className='mt-16'>
                                    <h6 className='text-md fw-semibold mb-12 text-primary-600 d-flex align-items-center gap-2'>
                                      <Icon icon='solar:document-add-linear' />
                                      {t("propina.aggregated.title")}
                                    </h6>
                                    <div className='table-responsive'>
                                      <table className='table table-bordered table-sm mb-0 bg-base'>
                                        <thead>
                                          <tr>
                                            <th className='bg-neutral-50' style={{ width: 28 }} />
                                            <th className='bg-neutral-50'>{t("propina.aggregated.col.date")}</th>
                                            <th className='bg-neutral-50'>{t("propina.col.total")}</th>
                                            <th className='bg-neutral-50'>{t("propina.aggregated.col.discount")}</th>
                                            <th className='bg-neutral-50'>{t("propina.aggregated.col.paid")}</th>
                                            <th className='bg-neutral-50'>{t("propina.aggregated.col.method")}</th>
                                            <th className='bg-neutral-50 text-end'>{t("global.col.actions")}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {row.aggregatedInvoices.map((invoice, idx) => {
                                            const key = `${row.registrationId}-${invoice.bulkPaymentId || idx}`;
                                            const isAggExpanded = expandedAggregatedKeys.includes(key);
                                            const monthlyValue =
                                              row.registrationMonthlyPayment || row.currentMonthlyPayment || 0;
                                            const propinasExpected =
                                              monthlyValue * (invoice.payments?.length || 0);
                                            const taxesExpected = (invoice.taxPayments || []).reduce(
                                              (sum, tp) => sum + (parseFloat(tp.taxValue) || 0),
                                              0,
                                            );
                                            const expectedTotal = propinasExpected + taxesExpected;
                                            const paidValue = parseFloat(invoice.totalAmount) || 0;
                                            const discount = Math.max(0, expectedTotal - paidValue);
                                            const printing = busyAggregatedPrint === key;
                                            return (
                                              <React.Fragment key={key}>
                                                <tr role='button' onClick={() => toggleAggregated(key)}>
                                                  <td>
                                                    <Icon
                                                      icon={
                                                        isAggExpanded
                                                          ? "ic:round-expand-less"
                                                          : "ic:round-expand-more"
                                                      }
                                                      className='text-secondary-light text-xl'
                                                    />
                                                  </td>
                                                  <td>
                                                    {invoice.paymentDate
                                                      ? new Date(invoice.paymentDate).toLocaleDateString("pt-PT")
                                                      : "-"}
                                                  </td>
                                                  <td>{formatCurrency(expectedTotal)}</td>
                                                  <td>
                                                    {discount > 0 ? (
                                                      <span className='text-warning-main'>
                                                        -{formatCurrency(discount)}
                                                      </span>
                                                    ) : (
                                                      "-"
                                                    )}
                                                  </td>
                                                  <td>
                                                    <strong className='text-success-main'>
                                                      {formatCurrency(paidValue)}
                                                    </strong>
                                                  </td>
                                                  <td>{invoice.paymentMethod || "-"}</td>
                                                  <td className='text-end' onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                      type='button'
                                                      className='btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1'
                                                      disabled={printing}
                                                      onClick={() => handlePrintAggregated(invoice, row, key)}
                                                    >
                                                      <Icon icon='solar:printer-linear' />
                                                      {printing ? "..." : t("propina.aggregated.action.print")}
                                                    </button>
                                                  </td>
                                                </tr>
                                                {isAggExpanded && (
                                                  <tr>
                                                    <td colSpan={7} className='p-0'>
                                                      <div className='p-12 bg-neutral-25 border-top'>
                                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                                          <thead>
                                                            <tr>
                                                              <th className='bg-neutral-50'>
                                                                {t("propina.aggregated.col.description")}
                                                              </th>
                                                              <th
                                                                className='bg-neutral-50 text-center'
                                                                style={{ width: 60 }}
                                                              >
                                                                {t("propina.aggregated.col.qty")}
                                                              </th>
                                                              <th className='bg-neutral-50'>
                                                                {t("propina.detail.monthlyValue")}
                                                              </th>
                                                              <th className='bg-neutral-50'>
                                                                {t("propina.aggregated.col.discount")}
                                                              </th>
                                                              <th className='bg-neutral-50'>
                                                                {t("propina.aggregated.col.paid")}
                                                              </th>
                                                              <th className='bg-neutral-50 text-center'>
                                                                {t("global.status")}
                                                              </th>
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            {(invoice.payments || []).map((p, i) => {
                                                              const itemPaid = parseFloat(p.total) || 0;
                                                              const itemDiscount = Math.max(
                                                                0,
                                                                monthlyValue - itemPaid,
                                                              );
                                                              return (
                                                                <tr key={`p-${p.id || i}`}>
                                                                  <td className='notranslate'>
                                                                    {monthShortName(p.month)}/{p.year}
                                                                  </td>
                                                                  <td className='text-center'>1</td>
                                                                  <td>{formatCurrency(monthlyValue)}</td>
                                                                  <td>
                                                                    {itemDiscount > 0 ? (
                                                                      <span className='text-warning-main'>
                                                                        -{formatCurrency(itemDiscount)}
                                                                      </span>
                                                                    ) : (
                                                                      "-"
                                                                    )}
                                                                  </td>
                                                                  <td>
                                                                    <strong className='text-success-main'>
                                                                      {formatCurrency(itemPaid)}
                                                                    </strong>
                                                                  </td>
                                                                  <td className='text-center'>
                                                                    <span className='px-8 py-2 radius-8 text-xs bg-success-focus text-success-main'>
                                                                      {t("propina.status.paid")}
                                                                    </span>
                                                                  </td>
                                                                </tr>
                                                              );
                                                            })}
                                                            {(invoice.taxPayments || []).map((tp, i) => {
                                                              // Older invoices never recorded a per-tax status (everything in
                                                              // an aggregated invoice was, by definition, paid) — default to
                                                              // Pago rather than mislabel them as Não Pago.
                                                              const tpStatus = tp.status != null && Number.isFinite(Number(tp.status)) ? Number(tp.status) : 1;
                                                              const isTpPartial = tpStatus === 2;
                                                              const tpPaidValue = isTpPartial
                                                                ? parseFloat(tp.paidAmount || 0)
                                                                : parseFloat(tp.taxValue || 0);
                                                              return (
                                                                <tr key={`t-${tp.id || i}`}>
                                                                  <td>{tp.taxName}</td>
                                                                  <td className='text-center'>1</td>
                                                                  <td>{formatCurrency(tp.taxValue)}</td>
                                                                  <td>-</td>
                                                                  <td>
                                                                    <strong className={isTpPartial ? "text-warning-main" : "text-success-main"}>
                                                                      {formatCurrency(tpPaidValue)}
                                                                    </strong>
                                                                  </td>
                                                                  <td className='text-center'>
                                                                    <span
                                                                      className={`px-8 py-2 radius-8 text-xs ${
                                                                        statusBadgeProps(tpStatus, t).className
                                                                      }`}
                                                                    >
                                                                      {statusBadgeProps(tpStatus, t).label}
                                                                    </span>
                                                                  </td>
                                                                </tr>
                                                              );
                                                            })}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("estudante.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`modal fade ${removeFineTarget ? "show d-block" : ""}`} tabIndex={-1} style={removeFineTarget ? { backgroundColor: "rgba(0,0,0,0.5)" } : {}}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content radius-16 bg-base'>
            <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
              <h6 className='modal-title mb-0'>{t("propina.removeFine.title")}</h6>
              <button type='button' className='btn-close' onClick={() => setRemoveFineTarget(null)} />
            </div>
            <div className='modal-body p-24'>
              <p className='text-secondary-light text-sm mb-16'>{t("propina.removeFine.confirmText")}</p>
              {removeFineTarget && (
                <div className='p-12 radius-8 bg-neutral-50 mb-16'>
                  <div className='row gy-2 text-sm'>
                    <div className='col-6'>
                      <strong>{t("propina.col.month")}:</strong> <span className='notranslate'>{monthName(removeFineTarget.month)}</span>
                    </div>
                    <div className='col-6'>
                      <strong>{t("propina.col.year")}:</strong> {removeFineTarget.year}
                    </div>
                    <div className='col-6'>
                      <strong>{t("propina.detail.reference")}:</strong> {removeFineTarget.reference || "-"}
                    </div>
                    <div className='col-6'>
                      <strong>{t("propina.detail.fine")}:</strong> {formatCurrency(removeFineTarget.fine)}
                    </div>
                    <div className='col-6'>
                      <strong>{t("propina.col.total")}:</strong>{" "}
                      {formatCurrency(removeFineTarget.combinedTotal)}
                    </div>
                  </div>
                </div>
              )}
              <label className='form-label'>{t("propina.removeFine.reason")} <span className='required-asterisk'>*</span></label>
              <textarea
                rows={4}
                maxLength={500}
                className='form-control'
                placeholder={t("propina.removeFine.reason.placeholder")}
                value={removeFineReason}
                onChange={(e) => setRemoveFineReason(e.target.value)}
              />
            </div>
            <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
              <button
                type='button'
                className='btn btn-neutral-500 border-neutral-100 px-24'
                onClick={() => setRemoveFineTarget(null)}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-danger px-24'
                onClick={handleConfirmRemoveFine}
                disabled={removingFine}
              >
                {removingFine ? "..." : t("propina.removeFine.confirm")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${editExtraTarget ? "show d-block" : ""}`}
        tabIndex={-1}
        style={editExtraTarget ? { backgroundColor: "rgba(0,0,0,0.5)" } : {}}
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content radius-16 bg-base'>
            <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
              <h6 className='modal-title mb-0'>{t("propina.extras.editTitle")}</h6>
              <button type='button' className='btn-close' onClick={() => setEditExtraTarget(null)} />
            </div>
            <div className='modal-body p-24'>
              {editExtraTarget && (
                <p className='text-secondary-light text-sm mb-16'>
                  {t("propina.extras.editSubtitle", {
                    name: editExtraTarget.extra.taxName || t("propina.extras.default"),
                  })}
                </p>
              )}
              <label className='form-label'>{t("propina.extras.amount")} <span className='required-asterisk'>*</span></label>
              <input
                type='number'
                min='0'
                step='0.01'
                className='form-control'
                value={editExtraAmount}
                onChange={(e) => setEditExtraAmount(e.target.value)}
              />
            </div>
            <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
              <button
                type='button'
                className='btn btn-neutral-500 border-neutral-100 px-24'
                onClick={() => setEditExtraTarget(null)}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary-600 px-24'
                onClick={handleConfirmEditExtra}
                disabled={savingExtraAmount}
              >
                {savingExtraAmount ? "..." : (<><i className='ri-save-line' /> {t("global.save")}</>)}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${payTaxRemainderTarget ? "show d-block" : ""}`}
        tabIndex={-1}
        style={payTaxRemainderTarget ? { backgroundColor: "rgba(0,0,0,0.5)" } : {}}
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content radius-16 bg-base'>
            <div className='modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0'>
              <h6 className='modal-title mb-0'>{t("propina.confirm.payTaxRemainderTitle")}</h6>
              <button type='button' className='btn-close' onClick={() => setPayTaxRemainderTarget(null)} />
            </div>
            <div className='modal-body p-24'>
              {payTaxRemainderTarget && (
                <div className='p-12 radius-8 bg-neutral-50 mb-16'>
                  <div className='row gy-2 text-sm'>
                    <div className='col-6'>
                      <strong>{t("bulk.col.taxName")}:</strong> {payTaxRemainderTarget.tax.taxName}
                    </div>
                    <div className='col-6'>
                      <strong>{t("propina.col.debt")}:</strong>{" "}
                      {formatCurrency(getTaxRemaining(payTaxRemainderTarget.tax))}
                    </div>
                  </div>
                </div>
              )}
              <div className='row gy-3'>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("propina.field.amountToPay")} <span className='required-asterisk'>*</span></label>
                  <input
                    type='number'
                    min='0.01'
                    max={payTaxRemainderTarget ? getTaxRemaining(payTaxRemainderTarget.tax) : undefined}
                    step='0.01'
                    className='form-control'
                    value={payTaxRemainderAmount}
                    onChange={(e) => setPayTaxRemainderAmount(e.target.value)}
                  />
                  {payTaxRemainderTarget &&
                    parseFloat(payTaxRemainderAmount) > 0 &&
                    parseFloat(payTaxRemainderAmount) < getTaxRemaining(payTaxRemainderTarget.tax) - 0.01 && (
                      <span className='text-warning-main text-xs'>
                        {t("bulk.field.amountPaid.remaining", {
                          value: formatCurrency(getTaxRemaining(payTaxRemainderTarget.tax) - parseFloat(payTaxRemainderAmount)),
                        })}
                      </span>
                    )}
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("propina.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
                  <select
                    className='form-control form-select'
                    value={payTaxRemainderMethod}
                    onChange={(e) => setPayTaxRemainderMethod(e.target.value)}
                  >
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6'>
                  <label className='form-label'>{t("propina.field.paymentDate")}</label>
                  <input
                    type='date'
                    max={new Date().toISOString().split("T")[0]}
                    className='form-control'
                    value={payTaxRemainderDate}
                    onChange={(e) => setPayTaxRemainderDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className='modal-footer border border-bottom-0 border-start-0 border-end-0'>
              <button
                type='button'
                className='btn btn-neutral-500 border-neutral-100 px-24'
                onClick={() => setPayTaxRemainderTarget(null)}
              >
                {t("global.cancel")}
              </button>
              <button
                type='button'
                className='btn btn-primary-600 px-24'
                onClick={handleConfirmPayTaxRemainder}
                disabled={payingTaxRemainder}
              >
                {payingTaxRemainder ? "..." : (<><i className='ri-check-line' /> {t("global.confirm")}</>)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListaPropinasLayer;
